import { useEffect, useRef } from "react";
import { parseAAMVA } from "@/lib/aamva";

// Global scan listener — accumulates fast keystrokes and detects AAMVA barcodes while the
// dialog is open in the "waiting" phase. Extracted from AgeCheckDialog for readability.
// Runs on ALL targets (including the manual DOB input) because keyboard-wedge scanners
// stream at ~1000+ chars/sec — much faster than any human typing. When a burst is detected
// (keys arriving < 25ms apart), the key is swallowed so it does not land in the DOB input.
export function useIdScanListener({ open, phase, onScan }) {
  const scanBufferRef = useRef("");
  const scanTimerRef = useRef(null);
  const lastKeyAtRef = useRef(0);

  useEffect(() => {
    if (!open || phase !== "waiting") return undefined;
    const onKey = (e) => {
      const now = Date.now();
      const dt = now - lastKeyAtRef.current;
      lastKeyAtRef.current = now;
      const isDobInput = e.target?.dataset?.calc === "0";
      const isBurst = dt < 25;

      // Some scanners (e.g. Motorola/Zebra DS9808) transmit AAMVA's
      // non-printable separators as Ctrl+key combos instead of Enter:
      // Ctrl+J = LF (0x0A), Ctrl+M = CR (0x0D), Ctrl+6/^ = RS (0x1E).
      // Without this, the base letter (j/m/6) gets typed literally,
      // corrupting every field boundary. Treat them as separators and
      // swallow all other Ctrl-combos during a scan so they don't fire
      // browser shortcuts (Ctrl+P, Ctrl+F, etc).
      if (e.ctrlKey && !isDobInput) {
        e.preventDefault();
        e.stopPropagation();
        const k = (e.key || "").toLowerCase();
        if (k === "j" || k === "m" || k === "6" || k === "^") {
          scanBufferRef.current += "\n";
          scheduleFlush();
        }
        return;
      }

      // Preserve Enter as newline (AAMVA field separator) — do NOT flush on
      // first CR. Flush is done by the idle timer.
      if (e.key === "Enter") {
        // If a scan is in progress, swallow the Enter and treat as newline.
        if ((isBurst && scanBufferRef.current.length > 0) || scanBufferRef.current.length > 5) {
          e.preventDefault();
          e.stopPropagation();
          scanBufferRef.current += "\n";
          scheduleFlush();
          return;
        }
        // Otherwise it's a human hitting Enter on the manual DOB — let the
        // input's own handler run and clear any buffer noise.
        scanBufferRef.current = "";
        return;
      }

      if (e.key && e.key.length === 1) {
        // While the dialog is open in the waiting phase, swallow ALL printable
        // keys unless the user is typing in the manual DOB input. This
        // prevents the AAMVA payload from leaking into the background POS
        // scan-input (or any other focused field).
        if (!isDobInput) {
          e.preventDefault();
          e.stopPropagation();
        }
        scanBufferRef.current += e.key;
        scheduleFlush();
      }
    };
    const scheduleFlush = () => {
      clearTimeout(scanTimerRef.current);
      scanTimerRef.current = setTimeout(() => {
        const raw = scanBufferRef.current;
        scanBufferRef.current = "";
        if (raw.length < 40) return; // too short to be a DL barcode
        onScan(raw);
      }, 250);
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      clearTimeout(scanTimerRef.current);
    };
    // eslint-disable-next-line
  }, [open, phase]);
}

export function parseScanRaw(raw) {
  return parseAAMVA(raw);
}
